import React, { useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { ScrollView, StyleSheet, View, Pressable } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import ThemedInput from "@/components/ThemedInput";
import { CurrencyInput } from "@/components/ui/CurrencyInput";
import Input from "@/components/ui/Input";
import { EmailInput } from "@/components/ui/EmailInput";
import { SSNInput } from "@/components/ui/SSNInput";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import Modal from "@/components/Modal";
import { ModalCard } from "@/components/Modals/ModalCard";
import { MarketsInput } from "@/components/inputs/MarketsInput";
import { RolesPicker } from "@/components/Pickers/RolesPicker";
import { PositionsInput } from "@/components/inputs/PositionsInput";
import { PhoneListEditor, PhoneType } from "@/components/screens/shared";
import useAuth from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase-client";
import useToast from "@/hooks/useToast";
import Chip from "@/components/ui/Chip";
import { MaterialCommunityIcons, MaterialIcons } from "@expo/vector-icons";
import { validatePhoneNumber } from "@/utils/phoneValidation";
import { UserStatusType } from "@/types/db/users";
import { theme } from "@/constants/themes";
import { DEFAULT_PASSWORD_REQUIREMENTS } from "@/types/db/tenants";

// Status options for dropdown
const STATUS_OPTIONS = [
  { text: "ONBOARDING", color: "todo" },
  { text: "ACTIVE", color: "complete" },
  { text: "INACTIVE", color: "neutral" },
  { text: "SEPARATE", color: "attention" },
  { text: "PENDING", color: "pending" },
] as const;

export function getUserStatusColor(status: string, colors: theme): string {
  const mapping: Record<string, string> = {
    ONBOARDING: colors.todo.border,
    ACTIVE: colors.complete.border,
    INACTIVE: colors.neutral.border,
    SEPARATE: colors.attention.border,
    PENDING: colors.pending.border,
  };
  return mapping[status.toUpperCase()] || colors.neutral.border;
}

export const USER_STATUS_OPTIONS = STATUS_OPTIONS;

export type UserForm = {
  first_name: string;
  middle_name?: string;
  last_name: string;
  email_address: string;
  preferred_name?: string;
  password?: string;
  email_confirm?: boolean;
  email_verified?: boolean;
  user_roles?: {
    role: {
      id: string;
      name: string;
      description?: string;
    };
  }[];
  user_positions?: {
    position: {
      id: string;
      name: string;
      description?: string;
      billing_rate?: number;
    };
    meta_data?: {
      pay_rate?: {
        rate: "hourly";
        amount: number;
      };
    };
  }[];
  meta_data: {
    user_type: "employee";
    job_title?: string;
    job_description?: string;
    status: UserStatusType;
    markets?: string[]; // Array of market IDs (matches table format)
    phones?: {
      type: "cell" | "home" | "work";
      number: string;
    }[];
    pay?: {
      type?: "hourly" | "salary";
      rate?: number;
    };
    private?: {
      ssn?: string;
      date_of_birth?: string;
      address?: {
        street?: string;
        city?: string;
        state?: string;
        zip?: string;
      };
    };
  };
};

export type UserPayload = { user: UserForm };

export interface UserCreateFormRef {
  updateFirstName: (name: string) => void;
  updateLastName: (name: string) => void;
  updateStatus: (status: string) => void;
  handleSave: () => Promise<void>;
  handleReset: () => void;
}

export const UserCreateForm = forwardRef<
  UserCreateFormRef,
  {
    initialUser?: Partial<UserForm>;
    onSave?: (payload: UserPayload) => Promise<void> | void;
    onChange?: (payload: UserPayload) => void;
    onDirtyChange?: (dirty: boolean) => void;
  }
>(({ initialUser, onSave, onChange, onDirtyChange }, ref) => {
  const { colors } = useTheme();
  const { tenantId } = useAuth();
  const toast = useToast();
  const [generatePassword, setGeneratePassword] = useState(true);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [tenantRequirements, setTenantRequirements] = useState(DEFAULT_PASSWORD_REQUIREMENTS);
  const [rolePickerVisible, setRolePickerVisible] = useState(false);
  const [emailError, setEmailError] = useState<string | undefined>();
  const [emailChecking, setEmailChecking] = useState(false);
  const [cellPhoneError, setCellPhoneError] = useState<string | undefined>();
  const [cellPhoneChecking, setCellPhoneChecking] = useState(false);

  const [user, setUser] = useState<UserForm>({
    first_name: initialUser?.first_name ?? "",
    middle_name: initialUser?.middle_name ?? "",
    last_name: initialUser?.last_name ?? "",
    email_address: initialUser?.email_address ?? "",
    preferred_name: initialUser?.preferred_name ?? "",
    password: initialUser?.password ?? "",
    email_confirm: initialUser?.email_confirm ?? true,
    email_verified: initialUser?.email_verified ?? true,
    user_roles: initialUser?.user_roles ?? [],
    user_positions: initialUser?.user_positions ?? [],
    meta_data: {
      user_type: "employee",
      job_title: initialUser?.meta_data?.job_title ?? "",
      job_description: initialUser?.meta_data?.job_description ?? "",
      status: initialUser?.meta_data?.status ?? "ONBOARDING",
      markets: initialUser?.meta_data?.markets ?? [],
      phones: initialUser?.meta_data?.phones ?? [],
      pay: initialUser?.meta_data?.pay ?? { type: "hourly", rate: 10.5 },
      private: {
        ssn: initialUser?.meta_data?.private?.ssn ?? "",
        date_of_birth: initialUser?.meta_data?.private?.date_of_birth ?? "",
        address: {
          street: initialUser?.meta_data?.private?.address?.street ?? "",
          city: initialUser?.meta_data?.private?.address?.city ?? "",
          state: initialUser?.meta_data?.private?.address?.state ?? "",
          zip: initialUser?.meta_data?.private?.address?.zip ?? "",
        },
      },
    },
  });

  useEffect(() => {
    onChange?.({ user });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Check if email already exists (debounced)
  useEffect(() => {
    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const email = user.email_address?.trim().toLowerCase();

    // Clear error and skip check if email is empty or invalid format
    if (!email || !EMAIL_REGEX.test(email)) {
      setEmailError(undefined);
      setEmailChecking(false);
      return;
    }

    setEmailChecking(true);
    const timeoutId = setTimeout(async () => {
      try {
        const { data, error } = await supabase.from("users").select("id").ilike("email_address", email).limit(1);

        if (error) {
          console.error("Error checking email:", error);
          setEmailError(undefined);
        } else if (data && data.length > 0) {
          setEmailError("Email already exists");
        } else {
          setEmailError(undefined);
        }
      } catch (err) {
        console.error("Error checking email:", err);
        setEmailError(undefined);
      } finally {
        setEmailChecking(false);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [user.email_address]);

  // Check if cell phone already exists (debounced)
  useEffect(() => {
    // Get cell phone from form data
    const cellPhone = user.meta_data.phones?.find((p) => p.type === "cell")?.number;

    // Normalize phone number for comparison (remove non-digits)
    const normalizedCell = cellPhone?.replace(/\D/g, "");

    // Clear error and skip check if phone is empty or too short
    if (!normalizedCell || normalizedCell.length < 10) {
      setCellPhoneError(undefined);
      setCellPhoneChecking(false);
      return;
    }

    setCellPhoneChecking(true);
    const timeoutId = setTimeout(async () => {
      try {
        // Search for users with matching cell phone in meta_data->phones array
        const { data, error } = await supabase.from("users").select("id, meta_data");

        if (error) {
          console.error("Error checking cell phone:", error);
          setCellPhoneError(undefined);
        } else if (data) {
          // Check if any user has this cell phone
          const exists = data.some((u) => {
            const phones = u.meta_data?.phones || [];
            return phones.some((p: any) => p.type === "cell" && p.number?.replace(/\D/g, "") === normalizedCell);
          });

          if (exists) {
            setCellPhoneError("Cell phone already exists");
          } else {
            setCellPhoneError(undefined);
          }
        }
      } catch (err) {
        console.error("Error checking cell phone:", err);
        setCellPhoneError(undefined);
      } finally {
        setCellPhoneChecking(false);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [user.meta_data.phones]);

  const handleReset = () => {
    const nextUser: UserForm = {
      first_name: initialUser?.first_name ?? "",
      middle_name: initialUser?.middle_name ?? "",
      last_name: initialUser?.last_name ?? "",
      email_address: initialUser?.email_address ?? "",
      preferred_name: initialUser?.preferred_name ?? "",
      user_roles: initialUser?.user_roles ?? [],
      user_positions: initialUser?.user_positions ?? [],
      meta_data: {
        user_type: "employee",
        job_title: initialUser?.meta_data?.job_title ?? "",
        job_description: initialUser?.meta_data?.job_description ?? "",
        status: initialUser?.meta_data?.status ?? "ONBOARDING",
        markets: initialUser?.meta_data?.markets ?? [],
        phones: initialUser?.meta_data?.phones ?? [],
        pay: initialUser?.meta_data?.pay ?? { type: "hourly", rate: 10.5 },
        private: {
          ssn: initialUser?.meta_data?.private?.ssn ?? "",
          date_of_birth: initialUser?.meta_data?.private?.date_of_birth ?? "",
          address: {
            street: initialUser?.meta_data?.private?.address?.street ?? "",
            city: initialUser?.meta_data?.private?.address?.city ?? "",
            state: initialUser?.meta_data?.private?.address?.state ?? "",
            zip: initialUser?.meta_data?.private?.address?.zip ?? "",
          },
        },
      },
    };
    setUser(nextUser);
    baseline.current = JSON.stringify({ user: nextUser });
  };

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    updateFirstName: (name: string) => {
      setUser((u) => ({ ...u, first_name: name }));
    },
    updateLastName: (name: string) => {
      setUser((u) => ({ ...u, last_name: name }));
    },
    updateStatus: (status: string) => {
      setUser((u) => ({ ...u, meta_data: { ...u.meta_data, status: status as UserStatusType } }));
    },
    handleSave,
    handleReset,
  }));

  useEffect(() => {
    const loadTenantData = async () => {
      if (!tenantId) return;

      try {
        const { data, error } = await supabase.from("tenants").select("meta_data").eq("id", tenantId).single();

        if (error) throw error;

        // Fetch password requirements
        if (data?.meta_data?.password_policy?.requirements) {
          setTenantRequirements({
            ...DEFAULT_PASSWORD_REQUIREMENTS,
            ...data.meta_data.password_policy.requirements,
          });
        }
      } catch (error) {
        console.error("Failed to fetch tenant data:", error);
      }
    };

    loadTenantData();
  }, [tenantId]);

  // Password validation based on tenant requirements
  const passwordRequirements = useMemo(
    () => ({
      minLength: (user.password?.length || 0) >= tenantRequirements.min_length,
      hasUppercase: !tenantRequirements.require_uppercase || /[A-Z]/.test(user.password || ""),
      hasLowercase: !tenantRequirements.require_lowercase || /[a-z]/.test(user.password || ""),
      hasNumber: !tenantRequirements.require_number || /[0-9]/.test(user.password || ""),
      hasSpecial: !tenantRequirements.require_special || /[!@#$%^&*(),.?":{}|<>]/.test(user.password || ""),
    }),
    [user.password, tenantRequirements],
  );

  const allPasswordRequirementsMet = Object.values(passwordRequirements).every(Boolean);
  const passwordsMatch = user.password === confirmPassword && confirmPassword.length > 0;

  const baseline = useRef(JSON.stringify({ user }));
  const dirty = useMemo(() => JSON.stringify({ user }) !== baseline.current, [user]);

  // Notify parent of dirty state changes
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const updateUser = (patch: Partial<UserForm>) => setUser((u) => ({ ...u, ...patch }));

  const updateMetaData = (patch: Partial<UserForm["meta_data"]>) =>
    setUser((u) => ({ ...u, meta_data: { ...u.meta_data, ...patch } }));

  const updatePrivateData = (patch: Partial<UserForm["meta_data"]["private"]>) =>
    setUser((u) => ({
      ...u,
      meta_data: {
        ...u.meta_data,
        private: { ...u.meta_data.private, ...patch },
      },
    }));

  const updateAddress = (patch: Partial<NonNullable<UserForm["meta_data"]["private"]>["address"]>) =>
    setUser((u) => ({
      ...u,
      meta_data: {
        ...u.meta_data,
        private: {
          ...u.meta_data.private,
          address: { ...u.meta_data.private?.address, ...patch },
        },
      },
    }));

  // Helper function to add a new phone
  const handleAddPhone = () => {
    const currentPhones = user.meta_data.phones || [];
    const usedTypes = new Set(currentPhones.map((p) => p.type));

    // Find first available type
    const availableTypes: PhoneType[] = ["cell", "home", "work"];
    const nextType = availableTypes.find((type) => !usedTypes.has(type));

    if (!nextType) {
      toast.showToast({
        title: "Maximum phones reached",
        message: "You can only add one phone per type (cell, home, work)",
        toastType: "warning",
      });
      return;
    }

    const newPhone = {
      type: nextType,
      number: "",
    };

    updateMetaData({
      phones: [...currentPhones, newPhone],
    });
  };

  const handleSave = async () => {
    // Validation with toast messages like in projects
    if (!user.first_name?.trim()) {
      toast.showToast({
        title: "Missing First Name",
        message: "Please enter a first name for this user",
        toastType: "warning",
      });
      return;
    }
    if (!user.last_name?.trim()) {
      toast.showToast({
        title: "Missing Last Name",
        message: "Please enter a last name for this user",
        toastType: "warning",
      });
      return;
    }
    if (!user.email_address?.trim()) {
      toast.showToast({
        title: "Missing Email Address",
        message: "Please enter an email address for this user",
        toastType: "warning",
      });
      return;
    }
    if (emailError) {
      toast.showToast({
        title: "Email Already Exists",
        message: "A user with this email address already exists",
        toastType: "error",
      });
      return;
    }
    if (cellPhoneError) {
      toast.showToast({
        title: "Cell Phone Already Exists",
        message: "A user with this cell phone number already exists",
        toastType: "error",
      });
      return;
    }

    // Validate phone numbers
    const phones = user.meta_data.phones || [];
    for (const phone of phones) {
      if (phone.number && !validatePhoneNumber(phone.number)) {
        toast.showToast({
          title: "Invalid phone number",
          message: `Please enter a valid phone number for ${phone.type}`,
          toastType: "error",
        });
        return;
      }
    }

    // Validate SSN if provided (optional field)
    const ssn = user.meta_data.private?.ssn || "";
    if (ssn && !/^\d{3}-\d{2}-\d{4}$/.test(ssn)) {
      toast.showToast({
        title: "Invalid SSN Format",
        message: "Please enter a valid SSN in the format XXX-XX-XXXX",
        toastType: "error",
      });
      return;
    }

    // Validate password if custom password is being set
    if (!generatePassword) {
      if (!allPasswordRequirementsMet) {
        toast.showToast({
          title: "Password Requirements Not Met",
          message: "Please ensure your password meets all requirements",
          toastType: "warning",
        });
        return;
      }
      if (!passwordsMatch) {
        toast.showToast({
          title: "Passwords Don't Match",
          message: "Please ensure both passwords match",
          toastType: "warning",
        });
        return;
      }
    }

    try {
      // Don't send password if auto-generating
      const userData = {
        ...user,
        password: generatePassword ? undefined : user.password,
      };
      await onSave?.({ user: userData });
      baseline.current = JSON.stringify({ user: userData });
    } catch (e: any) {
      // Error handling is now done in the parent component
      throw e;
    }
  };

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={{ gap: 8 }}>
        {/* Basic Information */}
        <View style={[styles.card, { borderColor: colors.border }]}>
          <ThemedText type="defaultSemiBold">Basic Information</ThemedText>
          <View style={{ gap: 8 }}>
            <View style={{ flexDirection: "row", gap: 8 }}>
              <ThemedInput
                testID="user-middle-name-input"
                style={{ flex: 1 }}
                label="Middle Name"
                placeholder="Middle name"
                value={user.middle_name || ""}
                onChangeText={(v) => updateUser({ middle_name: v })}
              />
            </View>
            <EmailInput
              label="Email Address"
              placeholder="email@example.com"
              value={user.email_address}
              onChangeText={(v) => updateUser({ email_address: v })}
              required
              error={emailError}
              checking={emailChecking}
            />
            <ThemedInput
              testID="user-preferred-name-input"
              label="Preferred Name"
              placeholder="Preferred name / Nickname"
              value={user.preferred_name || ""}
              onChangeText={(v) => updateUser({ preferred_name: v })}
            />
          </View>
        </View>

        {/* Contact Information */}
        <View style={[styles.card, { borderColor: colors.border }]}>
          <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
            <ThemedText type="defaultSemiBold">Contact Information</ThemedText>
            <Button
              testID="user-add-phone-button"
              text="Add Phone"
              icon="plus"
              onPress={handleAddPhone}
              disabled={(user.meta_data.phones?.length || 0) >= 3}
              style={{
                height: 32,
                paddingHorizontal: 12,
                backgroundColor: colors.primary,
              }}
              textStyle={{
                fontSize: 14,
              }}
            />
          </View>
          <PhoneListEditor
            phones={user.meta_data.phones || []}
            onChange={(phones) => updateMetaData({ phones })}
            editing={true}
            cellPhoneError={cellPhoneError}
            cellPhoneChecking={cellPhoneChecking}
          />
        </View>

        {/* Authentication Options */}
        <View style={[styles.card, { borderColor: colors.border }]}>
          <ThemedText type="defaultSemiBold">Authentication Options</ThemedText>
          <View style={{ gap: 12 }}>
            {/* Password Mode Toggle */}
            <View style={{ flexDirection: "row", gap: 8 }}>
              <Pressable
                testID="user-generate-password-button"
                onPress={() => {
                  setGeneratePassword(true);
                  setConfirmPassword("");
                }}
                style={[
                  styles.authToggle,
                  {
                    backgroundColor: generatePassword ? colors.primary : "transparent",
                    borderColor: generatePassword ? colors.primary : colors.border,
                  },
                ]}
              >
                <MaterialIcons name="autorenew" size={16} color={generatePassword ? "white" : colors.textSecondary} />
                <ThemedText style={{ color: generatePassword ? "white" : colors.text, fontSize: 13 }}>
                  Send Invite Email
                </ThemedText>
              </Pressable>
              <Pressable
                testID="user-custom-password-button"
                onPress={() => setGeneratePassword(false)}
                style={[
                  styles.authToggle,
                  {
                    backgroundColor: !generatePassword ? colors.primary : "transparent",
                    borderColor: !generatePassword ? colors.primary : colors.border,
                  },
                ]}
              >
                <MaterialIcons name="lock" size={16} color={!generatePassword ? "white" : colors.textSecondary} />
                <ThemedText style={{ color: !generatePassword ? "white" : colors.text, fontSize: 13 }}>
                  Set Password
                </ThemedText>
              </Pressable>
            </View>

            {generatePassword ? (
              <View style={[styles.infoBox, { backgroundColor: colors.sky?.background || colors.backgroundSecondary }]}>
                <MaterialIcons name="mail-outline" size={20} color={colors.sky?.text || colors.primary} />
                <ThemedText style={{ flex: 1, fontSize: 13, color: colors.sky?.text || colors.textSecondary }}>
                  User will receive an invitation email to set their own password.
                </ThemedText>
              </View>
            ) : (
              <>
                {/* Password Input */}
                <View style={styles.passwordInputContainer}>
                  <Input
                    testID="user-password-input"
                    icon="lock"
                    placeholder="Password"
                    value={user.password || ""}
                    onChangeText={(v) => updateUser({ password: v })}
                    secureTextEntry={!showPassword}
                    containerStyle={{
                      borderWidth: 1,
                      borderColor: user.password
                        ? allPasswordRequirementsMet
                          ? colors.emerald?.border || colors.primary
                          : colors.ruby?.border || "red"
                        : colors.border,
                    }}
                  />
                  <Pressable style={styles.eyeButton} onPress={() => setShowPassword(!showPassword)}>
                    <MaterialIcons
                      name={showPassword ? "visibility" : "visibility-off"}
                      size={20}
                      color={colors.textSecondary}
                    />
                  </Pressable>
                </View>

                {/* Confirm Password Input */}
                <View style={styles.passwordInputContainer}>
                  <Input
                    testID="user-confirm-password-input"
                    icon="lock"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChangeText={setConfirmPassword}
                    secureTextEntry={!showConfirmPassword}
                    containerStyle={{
                      borderWidth: 1,
                      borderColor: confirmPassword
                        ? passwordsMatch
                          ? colors.emerald?.border || colors.primary
                          : colors.ruby?.border || "red"
                        : colors.border,
                    }}
                  />
                  <Pressable style={styles.eyeButton} onPress={() => setShowConfirmPassword(!showConfirmPassword)}>
                    <MaterialIcons
                      name={showConfirmPassword ? "visibility" : "visibility-off"}
                      size={20}
                      color={colors.textSecondary}
                    />
                  </Pressable>
                </View>

                {confirmPassword.length > 0 && !passwordsMatch && (
                  <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
                    <MaterialIcons name="error" size={16} color={colors.ruby?.text || "red"} />
                    <ThemedText style={{ color: colors.ruby?.text || "red", fontSize: 13 }}>
                      Passwords do not match
                    </ThemedText>
                  </View>
                )}

                {/* Password Requirements */}
                <View
                  style={[
                    styles.requirementsBox,
                    { backgroundColor: colors.backgroundSecondary, borderColor: colors.border },
                  ]}
                >
                  <ThemedText style={{ fontSize: 13, fontWeight: "600", marginBottom: 8 }}>
                    Password Requirements
                  </ThemedText>
                  <PasswordRequirementRow
                    met={passwordRequirements.minLength}
                    text={`At least ${tenantRequirements.min_length} characters`}
                    colors={colors}
                  />
                  {tenantRequirements.require_uppercase && (
                    <PasswordRequirementRow
                      met={passwordRequirements.hasUppercase}
                      text="One uppercase letter"
                      colors={colors}
                    />
                  )}
                  {tenantRequirements.require_lowercase && (
                    <PasswordRequirementRow
                      met={passwordRequirements.hasLowercase}
                      text="One lowercase letter"
                      colors={colors}
                    />
                  )}
                  {tenantRequirements.require_number && (
                    <PasswordRequirementRow met={passwordRequirements.hasNumber} text="One number" colors={colors} />
                  )}
                  {tenantRequirements.require_special && (
                    <PasswordRequirementRow
                      met={passwordRequirements.hasSpecial}
                      text="One special character (!@#$%^&*)"
                      colors={colors}
                    />
                  )}
                </View>
              </>
            )}
          </View>
        </View>

        {/* Employment Information */}
        <View style={[styles.card, { borderColor: colors.border }]}>
          <ThemedText type="defaultSemiBold">Employment Information</ThemedText>
          <View style={{ gap: 8 }}>
            <ThemedInput
              testID="user-job-title-input"
              label="Job Title"
              placeholder="Job title"
              value={user.meta_data.job_title || ""}
              onChangeText={(v) => updateMetaData({ job_title: v })}
            />
            <ThemedInput
              testID="user-job-description-input"
              label="Job Description"
              placeholder="Job description"
              value={user.meta_data.job_description || ""}
              onChangeText={(v) => updateMetaData({ job_description: v })}
              multiline
              inputStyle={{ minHeight: 60 }}
            />

            <MarketsInput
              value={user.meta_data.markets || []}
              state="editing"
              onChange={(ids) => updateMetaData({ markets: ids })}
            />
          </View>
        </View>

        {/* Roles */}
        <View style={[styles.card, { borderColor: colors.border }]}>
          <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
            <ThemedText type="defaultSemiBold">Roles</ThemedText>
            <Button
              testID="user-add-role-button"
              text="Add Role"
              icon="plus"
              style={{
                height: 32,
                paddingHorizontal: 12,
                backgroundColor: colors.violet.border,
              }}
              onPress={() => setRolePickerVisible(true)}
            />
          </View>
          <View style={{ gap: 8 }}>
            {user.user_roles && user.user_roles.length > 0 ? (
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
                {user.user_roles.map((userRole, index) => (
                  <View key={userRole.role.id} style={{ flexDirection: "row", alignItems: "center" }}>
                    <Chip
                      text={userRole.role.name}
                      style={{ backgroundColor: colors.violet.background, borderColor: colors.violet.border }}
                    />
                    <Pressable
                      testID={`user-remove-role-${index}`}
                      onPress={() => {
                        const updated = user.user_roles?.filter((_, i) => i !== index);
                        setUser({ ...user, user_roles: updated });
                      }}
                      style={{
                        marginLeft: 4,
                        padding: 4,
                      }}
                    >
                      <MaterialCommunityIcons name="close-circle" size={20} color={colors.textSecondary} />
                    </Pressable>
                  </View>
                ))}
              </View>
            ) : (
              <ThemedText style={{ opacity: 0.5 }}>No roles assigned</ThemedText>
            )}
          </View>
        </View>

        {/* Positions */}
        <View style={[styles.card, { borderColor: colors.border }]}>
          <ThemedText type="defaultSemiBold">Positions</ThemedText>
          <PositionsInput
            value={user.user_positions ?? []}
            state="editing"
            onChange={(positions) => updateUser({ user_positions: positions })}
          />
        </View>

        {/* Pay Information */}
        <View style={[styles.card, { borderColor: colors.border }]}>
          <ThemedText type="defaultSemiBold">Pay Information</ThemedText>
          <View style={{ gap: 8 }}>
            <ThemedInput
              testID="user-pay-type-input"
              label="Pay Type"
              placeholder="Pay type"
              value={user.meta_data.pay?.type || "hourly"}
              onChangeText={(v) =>
                updateMetaData({
                  pay: { ...user.meta_data.pay, type: v as "hourly" | "salary" },
                })
              }
              items={[
                { text: "hourly", bgColor: colors.primary },
                { text: "salary", bgColor: colors.indigo.border },
              ]}
            />
            <CurrencyInput
              testID="user-pay-rate-input"
              label="Pay Rate"
              value={user.meta_data.pay?.rate ?? null}
              onChange={(v) =>
                updateMetaData({
                  pay: { ...user.meta_data.pay, rate: v ?? 0 },
                })
              }
            />
          </View>
        </View>

        {/* Private Information */}
        <View style={[styles.card, { borderColor: colors.border }]}>
          <ThemedText type="defaultSemiBold">Private Information (Optional)</ThemedText>
          <View style={{ gap: 8 }}>
            <SSNInput
              label="Social Security Number"
              value={user.meta_data.private?.ssn || ""}
              onChangeText={(v) => updatePrivateData({ ssn: v })}
            />
            <ThemedInput
              testID="user-date-of-birth-input"
              label="Date of Birth"
              placeholder="Date of birth"
              value={user.meta_data.private?.date_of_birth || ""}
              onChangeText={(v) => updatePrivateData({ date_of_birth: v })}
              type="date"
            />

            <ThemedText type="defaultSemiBold" style={{ marginTop: 8 }}>
              Address
            </ThemedText>
            <ThemedInput
              testID="user-address-street-input"
              label="Street Address"
              placeholder="Street address"
              value={user.meta_data.private?.address?.street || ""}
              onChangeText={(v) => updateAddress({ street: v })}
            />
            <View style={{ flexDirection: "row", gap: 8 }}>
              <ThemedInput
                testID="user-address-city-input"
                style={{ flex: 1 }}
                label="City"
                placeholder="City"
                value={user.meta_data.private?.address?.city || ""}
                onChangeText={(v) => updateAddress({ city: v })}
              />
              <ThemedInput
                testID="user-address-state-input"
                style={{ flex: 1 }}
                label="State"
                placeholder="State"
                value={user.meta_data.private?.address?.state || ""}
                onChangeText={(v) => updateAddress({ state: v })}
              />
            </View>
            <ThemedInput
              testID="user-address-zip-input"
              label="ZIP Code"
              placeholder="ZIP code"
              value={user.meta_data.private?.address?.zip || ""}
              onChangeText={(v) => updateAddress({ zip: v })}
              keyboardType="numeric"
            />
          </View>
        </View>
      </ScrollView>

      {/* Role Picker Modal */}
      {rolePickerVisible && (
        <Modal onUnmount={() => setRolePickerVisible(false)}>
          <ModalCard>
            <RolesPicker
              selectedIds={user.user_roles?.map((ur) => ur.role.id) || []}
              onPick={async (roleIds) => {
                // Fetch full role details for each ID
                const { data: roles, error } = await supabase.from("roles").select("*").in("id", roleIds);

                if (error) {
                  console.error("Failed to fetch role details:", error);
                  toast.showToast({
                    title: "Failed to fetch roles",
                    message: error.message,
                    toastType: "error",
                  });
                  setRolePickerVisible(false);
                  return;
                }

                // Map to user_roles structure
                const updated = roles.map((role) => ({
                  role: {
                    id: role.id,
                    name: role.name,
                    description: role.description || undefined,
                  },
                }));

                setUser({ ...user, user_roles: updated });
                setRolePickerVisible(false);
              }}
              onCancel={() => {
                setRolePickerVisible(false);
              }}
            />
          </ModalCard>
        </Modal>
      )}
    </View>
  );
});

UserCreateForm.displayName = "UserCreateForm";

function PasswordRequirementRow({ met, text, colors }: { met: boolean; text: string; colors: any }) {
  return (
    <View style={styles.requirementRow}>
      <MaterialIcons
        name={met ? "check-circle" : "radio-button-unchecked"}
        size={16}
        color={met ? colors.emerald?.text || colors.primary : colors.textSecondary}
      />
      <ThemedText style={{ fontSize: 12, color: met ? colors.text : colors.textSecondary }}>{text}</ThemedText>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  card: {
    gap: 8,
    padding: 12,
    borderRadius: 10,
    borderWidth: StyleSheet.hairlineWidth,
  },
  authToggle: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
  },
  infoBox: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    padding: 12,
    borderRadius: 8,
  },
  passwordInputContainer: {
    position: "relative",
  },
  eyeButton: {
    position: "absolute",
    right: 12,
    top: 0,
    bottom: 0,
    justifyContent: "center",
    padding: 4,
  },
  requirementsBox: {
    padding: 12,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
  },
  requirementRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 4,
  },
});
